import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Grid,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Typography,
  Divider,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FilterList,
  ExpandMore,
  ExpandLess,
  Clear
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Autocomplete from '@mui/material/Autocomplete';

const FilterBar = React.memo(({
  filters = {},
  onFiltersChange,
  filterConfig = [],
  advancedFilters = [],
  sx = {},
  paperSx = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasActiveAdvancedFilter = advancedFilters.some(config => {
    const val = filters[config.key];
    return Array.isArray(val) ? val.length > 0 : Boolean(val);
  });

  const [userClosedAdvanced, setUserClosedAdvanced] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchValues, setSearchValues] = useState({});

  // Automatically keep advanced panel open if any advanced filter is active
  useEffect(() => {
    if (hasActiveAdvancedFilter && !userClosedAdvanced) {
      setShowAdvanced(true);
    }
  }, [hasActiveAdvancedFilter, userClosedAdvanced]);
  const debounceTimers = useRef({});
  const isDirty = useRef({});

  // Only sync search input from external filters when the field is NOT being typed in
  // (e.g. when "Clear All" resets filters to empty string)
  useEffect(() => {
    [...filterConfig, ...advancedFilters].forEach(c => {
      if (c.type !== 'search' && c.type !== 'text') return;
      const externalVal = filters[c.key] ?? '';
      // If external value was cleared and field is not actively being edited, reset local value
      if (externalVal === '' && !isDirty.current[c.key]) {
        setSearchValues(prev => prev[c.key] !== '' ? { ...prev, [c.key]: '' } : prev);
      }
      if (externalVal === '') {
        isDirty.current[c.key] = false;
      }
    });
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters };

    // Set to empty/null instead of deleting to trigger state updates in parents
    if (value === '' || value === null) {
      const config = [...filterConfig, ...advancedFilters].find(c => c.key === key);
      newFilters[key] = config?.type === 'multiselect' ? [] : '';
    } else {
      newFilters[key] = value;
    }

    onFiltersChange(newFilters);
  };

  const hasActiveFilters = Object.values(filters).some(v =>
    Array.isArray(v) ? v.length > 0 : Boolean(v)
  );

  const clearAllFilters = () => {
    const resetFilters = {};
    [...filterConfig, ...advancedFilters].forEach(config => {
      resetFilters[config.key] = config.type === 'multiselect' ? [] : '';
    });
    onFiltersChange(resetFilters);
  };

  const renderFilter = (config) => {
    const value = filters[config.key] || (config.type === 'multiselect' ? [] : '');

    switch (config.type) {
      case 'text':
      case 'search': {
        const searchVal = searchValues[config.key] ?? (filters[config.key] || '');
        return (
          <TextField
            fullWidth
            label={config.label}
            placeholder={config.placeholder || config.label}
            value={searchVal}
            onChange={(e) => {
              const newVal = e.target.value;
              isDirty.current[config.key] = true;
              setSearchValues(prev => ({ ...prev, [config.key]: newVal }));
              clearTimeout(debounceTimers.current[config.key]);
              debounceTimers.current[config.key] = setTimeout(() => {
                isDirty.current[config.key] = false;
                handleFilterChange(config.key, newVal);
              }, 350);
            }}
            InputProps={{
              startAdornment: config.icon && (
                <InputAdornment position="start">{config.icon}</InputAdornment>
              )
            }}
          />
        );
      }

      case 'select':
        return (
          <FormControl fullWidth>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleFilterChange(config.key, e.target.value)}
              label={config.label}
            >
              {config.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'multiselect':
        return (
          <Autocomplete
            multiple
            options={config.options || []}
            getOptionLabel={(option) => option.label}
            value={config.options?.filter(opt => (value || []).includes(opt.value)) || []}
            onChange={(e, newValue) =>
              handleFilterChange(config.key, newValue.map(v => v.value))
            }
            renderInput={(params) => (
              <TextField {...params} label={config.label} />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
        );

      case 'date':
        return (
          <DatePicker
            label={config.label}
            value={value || null}
            onChange={(date) => handleFilterChange(config.key, date)}
            renderInput={(params) => <TextField {...params} fullWidth />}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined'
              }
            }}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={config.label}
            type="number"
            value={value || ''}
            onChange={(e) => handleFilterChange(config.key, e.target.value)}
            InputProps={config.prefix ? {
              startAdornment: <InputAdornment position="start">{config.prefix}</InputAdornment>
            } : undefined}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 3, ...sx }}>
      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
          </Typography>
          {Object.entries(filters).map(([key, value]) => {
            if (!value || (Array.isArray(value) && value.length === 0)) return null;

            const config = [...filterConfig, ...advancedFilters].find(c => c.key === key);
            let label = `${config?.label || key}: `;

            if (Array.isArray(value)) {
              if (value.length > 1) {
                label += `${value.length} selected`;
              } else {
                const opt = config?.options?.find(o => o.value === value[0]);
                label += opt ? opt.label : value[0];
              }
            } else if (value instanceof Date) {
              label += value.toLocaleDateString();
            } else {
              label += value;
            }

            return (
              <Chip
                key={key}
                label={label}
                onDelete={() => handleFilterChange(key, null)}
                size="small"
                sx={{
                  bgcolor: 'rgba(159, 232, 112, 0.15)',
                  color: 'secondary.main',
                  fontWeight: 600,
                  border: '1px solid rgba(22, 51, 0, 0.12)',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(22, 51, 0, 0.5)',
                    '&:hover': { color: 'secondary.main' }
                  }
                }}
              />
            );
          }).filter(Boolean)}
          <Button
            size="small"
            onClick={clearAllFilters}
            startIcon={<Clear />}
            sx={{
              ml: 1,
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '0.75rem',
              '&:hover': { color: '#D32F2F', bgcolor: 'rgba(211, 47, 47, 0.04)' }
            }}
          >
            Clear All
          </Button>
        </Box>
      )}

      <Paper sx={{ borderRadius: 2, border: '1px solid rgba(22, 51, 0, 0.12)', boxShadow: 'none', overflow: 'hidden', ...paperSx }}>
        {/* Basic Filters */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {filterConfig.map((config, index) => (
              <Grid item key={config.key} xs={12} md={4}>
                {renderFilter(config)}
              </Grid>
            ))}

            {/* Advanced toggle */}
            {advancedFilters.length > 0 && (
              <Grid item xs="auto">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterList />}
                  endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => {
                    const next = !showAdvanced;
                    setShowAdvanced(next);
                    if (!next) setUserClosedAdvanced(true);
                    else setUserClosedAdvanced(false);
                  }}
                  sx={{
                    borderColor: 'rgba(22, 51, 0, 0.12)',
                    color: 'secondary.main',
                    fontWeight: 700,
                    '&:hover': { borderColor: 'secondary.main', bgcolor: 'rgba(22, 51, 0, 0.04)' }
                  }}
                >
                  {isMobile ? '' : 'Advanced'}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Advanced Filters */}
        {advancedFilters.length > 0 && (
          <Collapse in={showAdvanced}>
            <Divider sx={{ borderColor: 'rgba(22, 51, 0, 0.08)' }} />
            <Box sx={{ p: 2, bgcolor: theme.palette.background.neutral || 'rgba(22, 51, 0, 0.02)' }}>
              <Grid container spacing={2} alignItems="center">
                {advancedFilters.map((config, index) => (
                  <Grid item key={config.key} xs={12} md={3}>
                    {renderFilter(config)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Collapse>
        )}
      </Paper>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only re-render if filters values changed (not reference), or onFiltersChange changed
  // This prevents focus loss when parent re-renders due to loading/data state changes
  return (
    JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters) &&
    prevProps.onFiltersChange === nextProps.onFiltersChange
  );
})

export default FilterBar;
